/**
 * AgreementServicesEditor — Day 24e (SA-01). One "+ Add Service" control with two sources:
 *   NDIS Service   → branch-enabled official NDIS item (Branch Catalogue → Support Item → OFFICIAL agreement line)
 *   Custom Service → organisation Service Catalogue item (CUSTOM agreement line; formerly the "seed line")
 * Reuses the official-line field set (OfficialAgreementLinesEditor.LineFields) and the DRAFT line-terms vocabulary.
 */
import React, { useState } from 'react';
import LineItemSelect from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import { Plus, Trash2, ShieldCheck, Wrench, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { LineFields, validateOfficialLines, officialLinesPayload, fmtMoney } from './OfficialAgreementLinesEditor';
import { RATE_BASES, FREQUENCIES } from './AgreementLineTermsEditor';

const RATE_BASIS_OPTIONS = RATE_BASES.map(([value, label]) => ({ value, label }));
const FREQUENCY_OPTIONS = FREQUENCIES.map(([value, label]) => ({ value, label }));

const num = (v) => (v === '' || v == null ? null : Number(v));
export const newService = (kind, agr) => ({
  kind, branch_catalogue_item_id: '', pricing_region: '', agreed_rate: '', allocated_funding: '',
  service_start_date: agr?.start || '', service_end_date: agr?.end || '',
  service_catalog_id: '', quantity: '', unit: 'hour', frequency: 'WEEKLY', rate_basis: 'FIXED_RATE', support_description: '',
});

export function validateServices(lines, enabled, agr) {
  const offErr = validateOfficialLines(lines.filter((l) => l.kind === 'NDIS'), enabled, agr);
  if (offErr) return offErr;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.kind !== 'CUSTOM') continue;
    if (!l.service_catalog_id) return `Service ${i + 1}: select a custom service`;
    const rate = num(l.agreed_rate);
    if (rate != null && rate < 0) return `Service ${i + 1}: agreed rate cannot be negative`;
    if (l.quantity !== '' && (isNaN(Number(l.quantity)) || Number(l.quantity) <= 0)) return `Service ${i + 1}: quantity must be positive`;
  }
  return null;
}

export function servicesPayload(lines, enabled) {
  const official = officialLinesPayload(lines.filter((l) => l.kind === 'NDIS'), enabled);
  const custom = lines.filter((l) => l.kind === 'CUSTOM').map((l) => ({
    service_catalog_id: l.service_catalog_id,
    quantity: l.quantity === '' ? undefined : Number(l.quantity), unit: l.unit || undefined, frequency: l.frequency || undefined,
    rate_basis: l.rate_basis || undefined, agreed_rate: l.agreed_rate === '' ? undefined : String(l.agreed_rate),
    allocated_funding: l.allocated_funding === '' ? undefined : String(l.allocated_funding),
    support_description: l.support_description || undefined,
    service_start_date: l.service_start_date || undefined, service_end_date: l.service_end_date || undefined,
  }));
  return [...official, ...custom];
}

function CustomFields({ idx, line, onChange, services }) {
  const set = (patch) => onChange({ ...line, ...patch });
  const t = (s) => `agreement-service-${idx}-${s}`;
  return (
    <div className="space-y-3">
      <div className="space-y-1"><Label>Custom service *</Label>
        <LineItemSelect value={line.service_catalog_id} onChange={(v) => set({ service_catalog_id: v })}
          options={services.map((s) => ({ value: String(s.id || s._id), label: `${s.name}${s.ndis_support_item_number || s.code ? ` · ${s.ndis_support_item_number || s.code}` : ''}${s.rate_per_unit != null ? ` (${fmtMoney(s.rate_per_unit)}/${s.unit || 'h'})` : ''}`,
            keywords: [s.code, s.ndis_support_item_number, s.support_item_number].filter(Boolean).join(' ') }))}
          placeholder="Select a service from the organisation catalogue" testId={t('custom-item')} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1"><Label>Rate basis</Label>
          <Select value={line.rate_basis} onValueChange={(v) => set({ rate_basis: v })}>
            <SelectTrigger data-testid={t('rate-basis')}><SelectValue /></SelectTrigger>
            <SelectContent>{RATE_BASIS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><Label>Agreed rate ($)</Label><Input type="number" step="0.01" min="0" value={line.agreed_rate} onChange={(e) => set({ agreed_rate: e.target.value })} data-testid={t('rate')} /></div>
        <div className="space-y-1"><Label>Quantity</Label><Input type="number" step="0.5" min="0" value={line.quantity} onChange={(e) => set({ quantity: e.target.value })} data-testid={t('quantity')} /></div>
        <div className="space-y-1"><Label>Frequency</Label>
          <Select value={line.frequency} onValueChange={(v) => set({ frequency: v })}>
            <SelectTrigger data-testid={t('frequency')}><SelectValue /></SelectTrigger>
            <SelectContent>{FREQUENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><Label>Allocated funding ($)</Label><Input type="number" step="0.01" min="0" value={line.allocated_funding} onChange={(e) => set({ allocated_funding: e.target.value })} data-testid={t('funding')} /></div>
        <div className="space-y-1"><Label>Line start</Label><TrinitiDatePicker value={line.service_start_date} onChange={(v) => set({ service_start_date: v })} testId={t('start')} /></div>
        <div className="space-y-1"><Label>Line end</Label><TrinitiDatePicker value={line.service_end_date} onChange={(v) => set({ service_end_date: v })} testId={t('end')} /></div>
        <div className="space-y-1"><Label>Description</Label><Input value={line.support_description} onChange={(e) => set({ support_description: e.target.value })} placeholder="Shown on the agreement" data-testid={t('description')} /></div>
      </div>
    </div>
  );
}

export function AgreementServicesEditor({ lines, onChange, enabled, services, agreementStart, agreementEnd, loading, branchLabel, canManageCatalogue, title = '3. Services', emptyHint }) {
  const [picking, setPicking] = useState(false);
  const byId = Object.fromEntries((enabled || []).map((e) => [e.id, e]));
  const update = (i, next) => onChange(lines.map((l, j) => (j === i ? next : l)));
  const remove = (i) => onChange(lines.filter((_, j) => j !== i));
  const add = (kind) => { onChange([...lines, newService(kind, { start: agreementStart, end: agreementEnd })]); setPicking(false); };
  const total = lines.reduce((s, l) => s + (num(l.allocated_funding) || 0), 0);
  const noNdis = !loading && (enabled || []).length === 0;
  return (
    <div className="space-y-3" data-testid="agreement-services">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">{title} <span className="text-muted-foreground font-normal">({lines.length})</span></Label>
        {!picking ? (
          <Button type="button" size="sm" onClick={() => setPicking(true)} data-testid="agreement-add-service"><Plus className="w-3.5 h-3.5 mr-1" /> Add Service</Button>
        ) : (
          <div className="flex gap-1" data-testid="agreement-add-service-picker">
            <Button type="button" size="sm" variant="outline" onClick={() => add('NDIS')} disabled={loading || noNdis} data-testid="agreement-add-service-ndis"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> NDIS Service</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => add('CUSTOM')} disabled={(services || []).length === 0} data-testid="agreement-add-service-custom"><Wrench className="w-3.5 h-3.5 mr-1" /> Custom Service</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setPicking(false)} data-testid="agreement-add-service-cancel">Cancel</Button>
          </div>
        )}
      </div>
      {loading && <p className="text-xs text-muted-foreground" data-testid="agreement-services-loading">Loading services enabled for this branch…</p>}
      {picking && noNdis && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="agreement-services-no-ndis">
          No NDIS services are enabled for {branchLabel || 'this branch'} in the Branch Catalogue, so <b>NDIS Service</b> is unavailable. Custom services can still be added.
          {canManageCatalogue && <Link to="/admin/branch-catalogue" className="ml-1 underline inline-flex items-center gap-0.5" data-testid="agreement-services-open-branch-catalogue">Open Branch Catalogue <ExternalLink className="h-3 w-3" /></Link>}
        </p>
      )}
      {lines.length === 0 && !picking && <p className="text-xs text-muted-foreground" data-testid="agreement-services-empty">{emptyHint || <>No services yet — use <b>Add Service</b> to add an NDIS or custom service. A draft can be saved without services.</>}</p>}
      {lines.map((l, i) => (
        <div key={i} className="rounded-md border p-3 space-y-3" data-testid={`agreement-service-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold flex items-center gap-2">Service {i + 1}
              <Badge variant="outline" className={l.kind === 'NDIS' ? 'border-primary/40 text-primary text-[10px]' : 'text-[10px]'} data-testid={`agreement-service-${i}-kind`}>{l.kind === 'NDIS' ? 'NDIS Service' : 'Custom Service'}</Badge></span>
            <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} data-testid={`agreement-service-${i}-remove`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
          {l.kind === 'NDIS' ? (
            <>
              <div className="space-y-1"><Label>NDIS support item (enabled for {branchLabel || 'branch'}) *</Label>
                <Select value={l.branch_catalogue_item_id} onValueChange={(v) => { const it = byId[v] || {}; update(i, { ...l, branch_catalogue_item_id: v, pricing_region: it.pricing_region || '', agreed_rate: it.unit_price_one ? '1' : (it.agreed_rate != null ? String(it.agreed_rate) : '') }); }}>
                  <SelectTrigger data-testid={`agreement-official-${i}-item`}><SelectValue placeholder="Select an NDIS support item" /></SelectTrigger>
                  <SelectContent>{(enabled || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.support_item_number} — {o.support_item_name?.slice(0, 60)}</SelectItem>)}</SelectContent>
                </Select></div>
              {l.branch_catalogue_item_id && <LineFields idx={i} line={l} item={byId[l.branch_catalogue_item_id]} onChange={(next) => update(i, next)} />}
            </>
          ) : <CustomFields idx={i} line={l} onChange={(next) => update(i, next)} services={services || []} />}
        </div>
      ))}
      {lines.length > 0 && <div className="flex justify-end text-sm" data-testid="agreement-services-total"><span className="text-muted-foreground mr-2">Allocated funding:</span><span className="font-semibold font-mono">{fmtMoney(total)}</span></div>}
    </div>
  );
}

export default AgreementServicesEditor;
